import { useState, useEffect } from 'react';
import { LogOut, Trash2, User, Plane as PlaneIcon, Activity, Info, Bell, BellOff, UserPlus, Check, CheckCircle2, FolderOpen, Radar, FolderX, Download, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { clearAll } from '../../lib/flightSyncDb';
import {
  listProfiles, switchProfile, removeProfile, updateActiveProfile,
} from '../../lib/flightSyncProfiles';
import { ensurePermission, notificationsSupported, notify } from '../../lib/flightSyncPush';
import { watchFolderSupported, pickWatchFolder, getWatchHandle, clearWatchFolder } from '../../lib/flightSyncWatch';
import { isInstalled, canInstall, subscribeInstall, promptInstall } from '../../lib/flightSyncInstall';
import FlightRecordHints from './FlightRecordHints';

export default function SettingsPage({ ctx, onReset, onChange }) {
  const navigate = useNavigate();
  const [pilotName, setPilotName] = useState(ctx?.pilot_name || '');
  const [droneModel, setDroneModel] = useState(ctx?.drone_model || '');
  const [droneSerial, setDroneSerial] = useState(ctx?.drone_serial || '');
  const [anac, setAnac] = useState(ctx?.anac || '');
  const [autoSync, setAutoSync] = useState(() => localStorage.getItem('fs_auto_sync') !== 'false');
  const [wifiOnly, setWifiOnly] = useState(() => localStorage.getItem('fs_wifi_only') === 'true');
  const [notifPermission, setNotifPermission] = useState(() =>
    notificationsSupported() ? Notification.permission : 'unsupported'
  );
  const [profiles, setProfiles] = useState(() => listProfiles());
  const [watchFolderName, setWatchFolderName] = useState(null);
  const watchSupported = watchFolderSupported();

  // Load watched folder name on mount
  useEffect(() => {
    getWatchHandle().then((h) => setWatchFolderName(h?.name || null));
  }, []);

  useEffect(() => {
    setPilotName(ctx?.pilot_name || '');
    setDroneModel(ctx?.drone_model || '');
    setDroneSerial(ctx?.drone_serial || '');
    setAnac(ctx?.anac || '');
  }, [ctx]);

  const saveProfile = () => {
    if (!ctx) return;
    const next = updateActiveProfile({
      pilot_name: pilotName, drone_model: droneModel, drone_serial: droneSerial, anac,
    });
    onChange(next);
    setProfiles(listProfiles());
    toast.success('Perfil atualizado');
  };

  const toggleAutoSync = () => {
    const v = !autoSync;
    setAutoSync(v);
    localStorage.setItem('fs_auto_sync', v ? 'true' : 'false');
  };

  const toggleWifiOnly = () => {
    const v = !wifiOnly;
    setWifiOnly(v);
    localStorage.setItem('fs_wifi_only', v ? 'true' : 'false');
  };

  const requestNotifications = async () => {
    const res = await ensurePermission();
    setNotifPermission(res);
    if (res === 'granted') {
      notify('FlightSync pronto', 'Voce sera notificado quando um voo for sincronizado.');
      toast.success('Notificacoes ativadas');
    } else if (res === 'denied') {
      toast.error('Permissao negada. Libere manualmente nas configuracoes do navegador.');
    }
  };

  const handleClearCache = async () => {
    if (!window.confirm('Remover TODOS os voos locais? Voos ja sincronizados permanecem no servidor.')) return;
    await clearAll();
    toast.success('Cache local limpo');
  };

  const handleSwitch = (id) => {
    const p = switchProfile(id);
    if (p) {
      onChange(p);
      setProfiles(listProfiles());
      toast.success(`Trocado para: ${p.label}`);
    }
  };

  const handleRemoveProfile = (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Remover este perfil do dispositivo?')) return;
    const remaining = removeProfile(id);
    setProfiles(remaining);
    if (remaining.length === 0) {
      onReset();
    } else if (id === ctx?.id) {
      onChange(remaining[0]);
    }
    toast.success('Perfil removido');
  };

  const handleAddProfile = () => {
    navigate('/flightsync/setup');
  };

  const handleFullReset = () => {
    if (!window.confirm('Sair e remover TODOS os perfis? Voce precisara colar o token novamente.')) return;
    onReset();
  };

  const handlePickFolder = async () => {
    try {
      const name = await pickWatchFolder();
      setWatchFolderName(name);
      toast.success(`Pasta monitorada: ${name}`);
    } catch (e) {
      if (e.name === 'AbortError') return;
      toast.error(e.message || 'Nao foi possivel selecionar a pasta');
    }
  };

  const handleClearWatch = async () => {
    await clearWatchFolder();
    setWatchFolderName(null);
    toast.success('Monitoramento desativado');
  };

  return (
    <div data-testid="fs-settings-page">
      <div className="fs-hero">
        <div className="fs-hero-label">CONFIGURACOES</div>
        <div className="fs-hero-value">Perfis & Sync</div>
      </div>

      {/* Multi-profile switcher */}
      <div className="fs-card" data-testid="fs-profiles-card">
        <div className="fs-card-title"><User size={12} /> PERFIS ({profiles.length})</div>
        {profiles.map((p) => {
          const isActive = p.id === ctx?.id;
          return (
            <div
              key={p.id}
              onClick={() => !isActive && handleSwitch(p.id)}
              className="fs-flight-item"
              style={{
                cursor: isActive ? 'default' : 'pointer',
                borderColor: isActive ? 'var(--fs-border-glow)' : 'var(--fs-border)',
                background: isActive ? 'rgba(0,212,255,0.08)' : 'rgba(5,6,13,0.5)',
              }}
              data-testid={`fs-profile-${p.id}`}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13 }}>
                  {p.label}
                  {isActive && <span className="fs-pill fs-pill-cyan">ATIVO</span>}
                </div>
                <div className="fs-flight-meta">{p.pilot_name || '-'} · {p.drone_model || '-'}</div>
              </div>
              {!isActive && (
                <button
                  onClick={(e) => handleRemoveProfile(p.id, e)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--fs-red)', cursor: 'pointer', padding: 6 }}
                  data-testid={`fs-remove-profile-${p.id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
              {isActive && <CheckCircle2 size={16} style={{ color: 'var(--fs-cyan)' }} />}
            </div>
          );
        })}
        <button
          className="fs-btn fs-btn-ghost"
          onClick={handleAddProfile}
          style={{ width: '100%', marginTop: 10 }}
          data-testid="fs-btn-add-profile"
        >
          <UserPlus size={14} /> ADICIONAR PERFIL
        </button>
      </div>

      {ctx && (
        <>
          <div className="fs-card">
            <div className="fs-card-title">PROJETO VINCULADO</div>
            <div className="fs-metric-row">
              <span className="fs-metric-label">Projeto</span>
              <span className="fs-metric-value">{ctx.project_name}</span>
            </div>
            <div className="fs-metric-row">
              <span className="fs-metric-label">Token</span>
              <span className="fs-metric-value" style={{ fontSize: 11 }}>{(ctx.token || '').slice(0, 12)}...</span>
            </div>
            <div className="fs-metric-row">
              <span className="fs-metric-label">Vinculado em</span>
              <span className="fs-metric-value" style={{ fontSize: 11 }}>
                {ctx.created_at ? new Date(ctx.created_at).toLocaleString('pt-BR') : '-'}
              </span>
            </div>
          </div>

          <div className="fs-card">
            <div className="fs-card-title"><PlaneIcon size={12} /> PERFIL DO PILOTO</div>
            <label className="fs-label">Nome</label>
            <input className="fs-input" value={pilotName} onChange={(e) => setPilotName(e.target.value)} data-testid="fs-settings-pilot-name" />
            <div style={{ height: 12 }} />
            <label className="fs-label">Modelo do drone</label>
            <input className="fs-input" value={droneModel} onChange={(e) => setDroneModel(e.target.value)} data-testid="fs-settings-drone-model" />
            <div style={{ height: 12 }} />
            <label className="fs-label">Serial</label>
            <input className="fs-input" value={droneSerial} onChange={(e) => setDroneSerial(e.target.value)} data-testid="fs-settings-drone-serial" />
            <div style={{ height: 12 }} />
            <label className="fs-label">CANAC / ANAC</label>
            <input className="fs-input" value={anac} onChange={(e) => setAnac(e.target.value)} data-testid="fs-settings-anac" />
            <div style={{ height: 14 }} />
            <button className="fs-btn fs-btn-primary" onClick={saveProfile} style={{ width: '100%' }} data-testid="fs-save-profile">
              <Check size={14} /> SALVAR PERFIL
            </button>
          </div>
        </>
      )}

      <div className="fs-card">
        <div className="fs-card-title"><Activity size={12} /> SINCRONIZACAO</div>
        <ToggleRow label="Sync automatico ao importar" checked={autoSync} onChange={toggleAutoSync} testid="fs-toggle-auto-sync" />
        <ToggleRow label="Apenas em WiFi" checked={wifiOnly} onChange={toggleWifiOnly} testid="fs-toggle-wifi-only" />
      </div>

      {/* Watch folder (File System Access API) - works on Chrome/Edge Android */}
      <div className="fs-card" data-testid="fs-watch-folder-card">
        <div className="fs-card-title">
          <Radar size={12} style={{ color: watchFolderName ? 'var(--fs-green)' : 'var(--fs-text-dim)' }} />
          {' '}MONITORAMENTO AUTOMATICO
        </div>
        <p style={{ fontSize: 12, color: 'var(--fs-text-dim)', margin: '0 0 10px 0', lineHeight: 1.5 }}>
          Escolha a pasta onde o DJI Fly/Pilot salva os logs. O FlightSync varre a cada 30s e envia os novos voos automaticamente.
        </p>
        {!watchSupported ? (
          <div style={{ fontSize: 12, color: 'var(--fs-amber)', padding: 8, background: 'rgba(245,158,11,0.08)', borderRadius: 8 }}>
            Navegador sem suporte. Use Chrome ou Edge no Android (inclui DJI RC, RC 2, RC Pro, RC Plus). No iPhone, use a funcao de Compartilhar.
          </div>
        ) : watchFolderName ? (
          <>
            <div className="fs-metric-row">
              <span className="fs-metric-label">Pasta ativa</span>
              <span className="fs-metric-value" style={{ fontSize: 12, color: 'var(--fs-green)' }}>
                <CheckCircle2 size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                {watchFolderName}
              </span>
            </div>
            <div className="fs-metric-row">
              <span className="fs-metric-label">Intervalo</span>
              <span className="fs-metric-value" style={{ fontSize: 12 }}>30s (e ao voltar ao app)</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="fs-btn fs-btn-ghost" onClick={handlePickFolder} style={{ flex: 1 }} data-testid="fs-btn-change-folder">
                <FolderOpen size={13} /> TROCAR PASTA
              </button>
              <button className="fs-btn fs-btn-danger" onClick={handleClearWatch} style={{ flex: 1 }} data-testid="fs-btn-clear-watch">
                <FolderX size={13} /> DESATIVAR
              </button>
            </div>
          </>
        ) : (
          <>
            <button className="fs-btn fs-btn-primary" onClick={handlePickFolder} style={{ width: '100%' }} data-testid="fs-btn-pick-folder">
              <FolderOpen size={14} /> ESCOLHER PASTA DO DJI
            </button>
          </>
        )}
      </div>

      {/* FlightRecord folder hints - shown BEFORE pilot picks a folder */}
      {watchSupported && !watchFolderName && <FlightRecordHints />}

      <div className="fs-card">
        <div className="fs-card-title">
          {notifPermission === 'granted' ? <Bell size={12} style={{ color: 'var(--fs-green)' }} /> : <BellOff size={12} />}
          {' '}NOTIFICACOES
        </div>
        <p style={{ fontSize: 12, color: 'var(--fs-text-dim)', margin: '0 0 10px 0' }}>
          Status: <strong style={{ color: notifPermission === 'granted' ? 'var(--fs-green)' : notifPermission === 'denied' ? 'var(--fs-red)' : 'var(--fs-amber)' }}>
            {notifPermission === 'granted' ? 'Ativadas' : notifPermission === 'denied' ? 'Bloqueadas' : notifPermission === 'unsupported' ? 'Nao suportadas' : 'Nao solicitadas'}
          </strong>
        </p>
        {notifPermission !== 'granted' && notifPermission !== 'unsupported' && (
          <button
            className="fs-btn fs-btn-primary"
            style={{ width: '100%' }}
            onClick={requestNotifications}
            data-testid="fs-btn-enable-notifications"
          >
            <Bell size={14} /> ATIVAR NOTIFICACOES
          </button>
        )}
      </div>

      <div className="fs-card">
        <div className="fs-card-title" style={{ color: 'var(--fs-amber)' }}><Info size={12} /> SOBRE</div>
        <div className="fs-metric-row">
          <span className="fs-metric-label">App</span>
          <span className="fs-metric-value">FlightSync v1.3.0</span>
        </div>
        <div className="fs-metric-row">
          <span className="fs-metric-label">Endpoint</span>
          <span className="fs-metric-value" style={{ fontSize: 11 }}>/api/flight-data/ingest-rich</span>
        </div>
        <div className="fs-metric-row">
          <span className="fs-metric-label">Formatos</span>
          <span className="fs-metric-value">.csv · .txt · .dat</span>
        </div>
        <div className="fs-metric-row">
          <span className="fs-metric-label">Compressao GPS</span>
          <span className="fs-metric-value">gzip (&gt;1000 pts)</span>
        </div>
      </div>

      <InstallPWACard />

      <button className="fs-btn fs-btn-ghost" onClick={() => navigate('/flightsync/help')} style={{ width: '100%', marginBottom: 10 }} data-testid="fs-btn-help">
        <Info size={14} /> COMO SINCRONIZAR (TUTORIAL)
      </button>

      <button className="fs-btn fs-btn-danger" onClick={handleClearCache} style={{ width: '100%', marginBottom: 10 }} data-testid="fs-btn-clear-cache">
        <Trash2 size={14} /> LIMPAR CACHE LOCAL
      </button>
      <button className="fs-btn fs-btn-ghost" onClick={handleFullReset} style={{ width: '100%' }} data-testid="fs-btn-reset">
        <LogOut size={14} /> SAIR E REMOVER TODOS OS PERFIS
      </button>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px dashed var(--fs-border)' }}>
      <span style={{ fontSize: 13 }}>{label}</span>
      <button
        type="button"
        onClick={onChange}
        data-testid={testid}
        aria-pressed={checked}
        style={{
          position: 'relative', width: 48, height: 26,
          borderRadius: 13, cursor: 'pointer', border: 'none',
          background: checked ? 'var(--fs-cyan)' : 'rgba(255,255,255,0.15)',
          transition: 'background 0.2s',
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: checked ? 25 : 3,
          width: 20, height: 20, borderRadius: '50%',
          background: '#0b0e1a', transition: 'left 0.2s',
        }} />
      </button>
    </div>
  );
}

function InstallPWACard() {
  const [state, setState] = useState(() => ({
    installed: isInstalled(),
    available: canInstall(),
  }));

  useEffect(() => {
    const unsub = subscribeInstall((evt) => {
      setState({ installed: evt.type === 'installed' || isInstalled(), available: evt.type === 'available' });
    });
    return () => unsub();
  }, []);

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') toast.success('FlightSync instalado');
    if (outcome === 'unavailable') {
      toast.info('No Chrome do Android: menu > "Adicionar a tela inicial". No iPhone: Safari > Compartilhar > "Adicionar a tela inicial".');
    }
  };

  if (state.installed) {
    return (
      <div className="fs-card" style={{ borderColor: 'rgba(16,185,129,0.3)' }} data-testid="fs-pwa-installed">
        <div className="fs-card-title" style={{ color: 'var(--fs-green)' }}>
          <CheckCircle2 size={12} /> APP INSTALADO
        </div>
        <p style={{ fontSize: 12, color: 'var(--fs-text-dim)', margin: 0, lineHeight: 1.5 }}>
          Seu token e perfil ficam salvos e <strong>nao precisam ser digitados novamente</strong> depois de desligar/ligar o controle.
        </p>
      </div>
    );
  }

  return (
    <div className="fs-card" style={{ borderColor: 'var(--fs-border-glow)', background: 'rgba(0,212,255,0.04)' }} data-testid="fs-pwa-install-card">
      <div className="fs-card-title">
        <Smartphone size={12} /> INSTALAR COMO APP
      </div>
      <p style={{ fontSize: 12, color: 'var(--fs-text-dim)', margin: '0 0 10px 0', lineHeight: 1.5 }}>
        <strong>Importante:</strong> instale o FlightSync na tela inicial para que seu login nao seja perdido ao desligar o controle.
      </p>
      <button className="fs-btn fs-btn-primary" onClick={handleInstall} style={{ width: '100%' }} data-testid="fs-btn-install-pwa">
        <Download size={14} /> INSTALAR FLIGHTSYNC
      </button>
      <p style={{ fontSize: 10, color: 'var(--fs-text-dim)', margin: '8px 0 0 0', lineHeight: 1.5 }}>
        Se nada acontecer, use o menu do navegador: <em>Adicionar a tela inicial</em>.
      </p>
    </div>
  );
}
